<template>
  <div>
    <PTransparentLoader :loading="loading">
      <CRow>
        <CCol lg="12">
          <CCard class="no-borders">
            <CCardHeader>
              <h1 class="main-header mb-0">
                <translate translate-context="chargeback.list">Chargebacks</translate>
              </h1>
              <div class="card-header-actions">
                <PButton
                    v-if="canExport"
                    class="btn btn-outline-primary mr-2"
                    @click="onExport"
                    :loading="exporting"
                    :loading-text="$gettext('Exporting...')"
                    spinner-color="primary"
                >
                  <translate translate-context="chargeback.list">Export</translate>
                </PButton>
                <CButton
                    v-if="canCreate"
                    color="primary"
                    @click="openAddModal"
                >
                  <CIcon name="cil-plus" class="mr-1"/>
                  <translate translate-context="chargeback.list">Add Chargeback</translate>
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody class="px-0">
              <ChargebackStatistics
                  v-if="statistics"
                  :statistics="statistics"
                  class="px-3 mb-3"
              />

              <AjaxTable
                  v-if="isReady"
                  ref="ajaxTable"
                  :fields="fields"
                  :data="chargebacks"
                  :fetch="fetchList"
                  table-name="chargebacks"
                  :context-key="contextKey"
                  :sync-url="true"
                  class="custom-table--clickable"
                  @row-clicked="onRowClick"
              >
                <template #effective_date="{item}">
                  <td>
                    {{ formatDate(item.effective_date) }}
                  </td>
                </template>

                <template #order="{item}">
                  <td>
                    <div>
                      <span class="font-weight-bold" v-if="item.order_number">
                        {{ item.order_number }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </td>
                </template>

                <template #event_name="{item}">
                  <td>
                    <span v-if="item.pica_service_name">{{ item.pica_service_name }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #customer="{item}">
                  <td>
                    <div v-if="item.user_email">
                      <div>{{ item.user_email }}</div>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #total_loss="{item}">
                  <td class="text-right">
                    <strong class="text-danger">{{ formatAmountWithCurrency(item.total_loss, item.currency) }}</strong>
                  </td>
                </template>

                <template #reason="{item}">
                  <td>
                    <div
                        v-if="item.reason"
                        class="text-truncate"
                        style="max-width: 200px;"
                        v-c-tooltip="item.reason"
                    >
                      {{ item.reason }}
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #created_at="{item}">
                  <td>
                    <div>
                      {{ item.created_at | formatDateTime }}
                      <div class="small text-muted" v-if="item.created_by_email">
                        {{ item.created_by_email }}
                      </div>
                    </div>
                  </td>
                </template>
              </AjaxTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PTransparentLoader>

    <AddChargebackModal
        v-if="canCreate"
        ref="addModal"
        @created="onChargebackCreated"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import PButton from '@/components/PButton.vue'
import ChargebackStatistics from './components/ChargebackStatistics.vue'
import AddChargebackModal from './components/AddChargebackModal.vue'
import { getPermissionPrefix } from '../constants'
import ConfigScope from "@/domain/core/constant/configScope"

export default {
  name: 'ChargebackList',
  components: {
    AjaxTable,
    PTransparentLoader,
    PButton,
    ChargebackStatistics,
    AddChargebackModal,
  },
  data() {
    return {
      loading: false,
      isReady: false,
    }
  },
  async mounted() {
    this.loading = true
    try {
      await this.setScope(this.scope)
      await this.fetchScopedConfig()
      await this.fetchStatistics(this.statisticsFilters)
      this.isReady = true
    } finally {
      this.loading = false
    }
  },
  computed: {
    ...mapGetters('accounting/chargeback', ['chargebacks', 'statistics', 'exporting']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    contextKey() {
      if (this.scopeType === 'global') return 'global'
      return `${this.scopeType}-${this.scopeId}`
    },

    permissionPrefix() {
      return getPermissionPrefix(this.scopeType)
    },

    canCreate() {
      return this.scopeType === 'global' && this.hasPerm(`${this.permissionPrefix}.accounting.chargeback.create`)
    },

    canExport() {
      return this.scopeType === 'global' && this.hasPerm(`${this.permissionPrefix}.accounting.chargeback.export`)
    },

    statisticsFilters() {
      if (!this.$refs.ajaxTable) return {}
      const filters = this.$refs.ajaxTable.getFilters()
      const result = {}
      if (filters.date_from) result.date_from = filters.date_from
      if (filters.date_to) result.date_to = filters.date_to
      return result
    },

    defaultDateRange() {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      const fmt = d => d.toISOString().slice(0, 10)
      return { start: fmt(start), end: fmt(end) }
    },

    fields() {
      const cols = [
        {
          key: 'effective_date',
          label: this.$pgettext('chargeback.list', 'Date'),
          _style: 'width: 140px;',
          sorter: true,
          dateRangeFrom: 'date_from',
          dateRangeTo: 'date_to',
          defaultValue: this.defaultDateRange,
        },
        {
          key: 'order',
          label: this.$pgettext('chargeback.list', 'Order'),
          _style: 'width: 140px;',
          filter: false,
          sorter: false,
        },
        {
          key: 'event_name',
          label: this.$pgettext('chargeback.list', 'Event'),
          sorter: false,
        },
      ]

      if (this.scopeType !== 'pica-service') {
        cols.push({
          key: 'customer',
          label: this.$pgettext('chargeback.list', 'Customer'),
          sorter: false,
          filter: false,
        })
      }

      cols.push(
        {
          key: 'total_loss',
          label: this.$pgettext('chargeback.list', 'Total Loss'),
          _style: 'width: 130px;',
          sorter: true,
          filter: false,
        },
        {
          key: 'reason',
          label: this.$pgettext('chargeback.list', 'Reason'),
          _style: 'width: 200px;',
          filter: false,
        },
        {
          key: 'created_at',
          label: this.$pgettext('chargeback.list', 'Created'),
          _style: 'width: 160px;',
          sorter: true,
        },
      )

      return cols
    },

    detailRouteName() {
      if (this.scopeType === 'contract-manager') return 'contractManager.chargebacks.detail'
      if (this.scopeType === 'pica-service') return 'event.chargebacks.detail'
      return 'admin.chargebacks.detail'
    },
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/chargeback', [
      'fetchChargebacks',
      'fetchStatistics',
      'exportChargebacks',
    ]),
    ...mapActions('login', ['fetchConfig']),

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    async fetchList({ filters, pagination, sorting }) {
      const mappedSorting = { ...sorting }
      if (mappedSorting.column === 'total_loss') {
        mappedSorting.column = 'chargeback_amount'
      }

      await this.fetchChargebacks({ filters, pagination, sorting: mappedSorting })

      const statsFilters = {}
      if (filters.date_from) statsFilters.date_from = filters.date_from
      if (filters.date_to) statsFilters.date_to = filters.date_to
      await this.fetchStatistics(statsFilters)
    },

    onRowClick(item) {
      const params = { id: item.id }
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({ name: this.detailRouteName, params })
    },

    openAddModal() {
      this.$refs.addModal.open()
    },

    async onChargebackCreated() {
      await this.$refs.ajaxTable?.fetchList()
    },

    async onExport() {
      const filters = this.$refs.ajaxTable
        ? this.$refs.ajaxTable.getFilters()
        : {}

      if (!filters.date_from) {
        const oneYearAgo = new Date()
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
        filters.date_from = oneYearAgo.toISOString().split('T')[0]
      }
      if (!filters.date_to) {
        filters.date_to = new Date().toISOString().split('T')[0]
      }

      await this.exportChargebacks(filters)
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    formatAmountWithCurrency(amount, currency) {
      const num = parseFloat(amount) || 0
      return `${num.toFixed(2)} ${currency || 'EUR'}`
    },
  },
}
</script>

<style scoped>
.custom-table--clickable >>> tbody tr {
  cursor: pointer;
}
.custom-table--clickable >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
